import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const mb = (b) => `${((Number(b) || 0) / 1048576).toFixed(1)} MB`;
const KIND_LABEL = { documents: 'Deal documents', contact_documents: 'Contact documents' };

/**
 * Settings → File Storage (10/28): where uploads live — the Railway volume and / or Cloudflare R2 — with the
 * one-time "copy the volume to R2" button. Super-admin only (the API router is gated the same way).
 */
export default function FileStorageSettings() {
  const { getAuthHeaders } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/storage/status`, { headers: getAuthHeaders() });
      setStatus(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not read storage status'); }
  }, [getAuthHeaders]);
  useEffect(() => { load(); }, [load]);

  const sync = async (dryRun) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/storage/sync`, { dry_run: dryRun }, { headers: getAuthHeaders() });
      if (r.data.error) { toast.error(r.data.error); return; }
      setPlan(r.data);
      if (!dryRun) {
        const copied = Object.values(r.data.kinds || {}).reduce((n, k) => n + (k.copied || 0), 0);
        const failed = Object.values(r.data.kinds || {}).reduce((n, k) => n + (k.failed_count || 0), 0);
        toast[failed ? 'error' : 'success'](`Copied ${copied} file(s) to R2${failed ? `, ${failed} failed` : ''}`);
        await load();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Sync failed'); } finally { setBusy(false); }
  };

  const totalMissing = status ? Object.values(status.kinds || {}).reduce((n, k) => n + (k.missing_in_r2 || 0), 0) : 0;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">File storage</CardTitle>
            <div className="flex gap-2">
              {status && <Badge variant="outline">reads: {status.backend === 'r2' ? 'R2 first, volume fallback' : 'volume first, R2 fallback'}</Badge>}
              {status && <Badge className={status.r2_configured ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>
                {status.r2_configured ? 'R2 connected' : 'R2 not configured'}</Badge>}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p className="text-slate-600">
            Every new upload is written to the Railway volume and, when R2 is connected, to Cloudflare R2 as well.
            Files are served from whichever holds them. The copy below moves the backlog that predates R2.
            The read order is set by the <code>STORAGE_BACKEND</code> variable on the API service (<code>local</code> or <code>r2</code>).
          </p>
          {status?.r2_error && (
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
              R2 refused the connection: {status.r2_error}. Check the four R2_* variables on the API service (no spaces, the endpoint
              starting with https://, the token scoped to the bucket).
            </p>
          )}
          {status && (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-500"><th className="py-1">Kind</th><th>On the volume</th><th>In R2</th><th>Not yet in R2</th></tr></thead>
              <tbody>
                {Object.entries(status.kinds || {}).map(([kind, k]) => (
                  <tr key={kind} className="border-t">
                    <td className="py-1">{KIND_LABEL[kind] || kind}</td>
                    <td>{k.local_files} · {mb(k.local_bytes)}</td>
                    <td>{k.r2_files} · {mb(k.r2_bytes)}</td>
                    <td className={k.missing_in_r2 ? 'text-amber-700 font-medium' : 'text-emerald-700'}>{k.missing_in_r2} · {mb(k.missing_bytes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={busy}>Refresh</Button>
            <Button variant="outline" size="sm" onClick={() => sync(true)} disabled={busy || !status?.r2_configured}>Preview copy</Button>
            <Button size="sm" onClick={() => { if (window.confirm(`Copy ${totalMissing} file(s) from the volume to R2? Nothing is deleted.`)) sync(false); }}
                    disabled={busy || !status?.r2_configured || !totalMissing}>Copy volume → R2</Button>
          </div>
          {plan && (
            <div className="text-xs text-slate-600">
              {plan.dry_run ? 'Preview' : 'Result'}: {Object.entries(plan.kinds || {}).map(([kind, k]) =>
                `${KIND_LABEL[kind] || kind}: ${k.to_copy} to copy (${mb(k.bytes)})${plan.dry_run ? '' : `, ${k.copied} copied, ${k.failed_count} failed`}`).join(' · ')}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
